import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Table, Pager, TableColumn, TableSort } from "@foris/avocado-suite";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { Context } from "@modules/sections/GroupsManager/context/GroupsManagerContext";
import { LinkLogEntryOrderByFieldsEnum, OrderByDirection } from "@models/ISchema";
import { LinkLogEntry } from "@models/ISchema";
import {
  getLinkCell,
  getLinkDisabledCell,
  getColumnCell,
  capitalizeFirstLetter,
  getColumnCellList,
  getUserCell,
} from "@common/utils/table.util";
import { Types as TableFiltersTypes } from "@modules/sections/GroupsManager/context/tableFilters.reducer";
import scss from "./linkHistoryTable.module.scss";

/**
 * Props interface for the LinkHistoryTable component
 */
interface LinkHistoryTableProps {
  /** Array of link history entries to display in the table */
  data: LinkLogEntry[];
  /** Pagination state containing page, size, and total count */
  pagination?: any;
  /** Configuration object for searchable columns */
  columnsToSearchBy?: {
    Fecha: boolean;
    Liga: boolean;
    "Recurso ID": boolean;
    Responsable: boolean;
  };
  /** Callback to update searchable columns configuration */
  setColumnsToSearchBy?: (columns: any) => void;
  /**
   * Callback triggered when table data needs to be reloaded
   * @param page - Page number to load
   * @param newOrderBy - New sorting configuration
   * @param searchBy - Search criteria
   */
  onLoadLogEntries?: (
    page: number,
    newOrderBy: {
      header: string;
      field: string;
      direction: OrderByDirection;
    },
    searchBy: {
      fields: string[];
      text: string;
    },
  ) => void;
  /** Callback when user changes the number of items per page */
  onChangeSizePerPage?: (value: number) => void;
}

/**
 * LinkHistoryTable displays a paginated table of link modification history
 *
 * Handles sorting, pagination, and rendering of link change entries with type-safe
 * rendering functions for each column. Provides navigation to the link editor
 * for enabled links and visual indicators for disabled links.
 */
const LinkHistoryTable = ({
  data = [],
  onLoadLogEntries,
  pagination,
  onChangeSizePerPage,
}: LinkHistoryTableProps) => {
  const { state, dispatch } = useContext(Context);
  const { t } = useTranslation();
  const { editorUrl } = useNavigationUrl();
  const handleChangeSizePerPage = (value: number) => {
    onChangeSizePerPage?.(value);
  };

  /**
   * Table column definitions with type-safe rendering functions
   * Each column uses the appropriate rendering utility based on data type
   */
  const columns = [
    {
      key: LinkLogEntryOrderByFieldsEnum.Date,
      header: t("link-changes-history.table.date"),
      renderCell: (data: LinkLogEntry) => getColumnCell(data?.date),
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "161px" },
      },
    },
    {
      key: LinkLogEntryOrderByFieldsEnum.LinkCode,
      header: t("link-changes-history.table.link"),
      renderCell: (data: LinkLogEntry) => {
        const entry = data as LinkLogEntry;
        const isLinkEnabled = entry?.link?.enabled;
        return isLinkEnabled
          ? getLinkCell(editorUrl("link", entry?.link?.id), null, entry?.link?.code)
          : getLinkDisabledCell(entry?.link?.code);
      },
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "180px" },
      },
    },
    {
      header: t("link-changes-history.table.resource"),
      renderCell: (data: LinkLogEntry) => getColumnCell(capitalizeFirstLetter(data?.resource)),
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "165px" },
      },
    },
    {
      key: LinkLogEntryOrderByFieldsEnum.ObjectId,
      header: t("link-changes-history.table.object-id"),
      renderCell: (data: LinkLogEntry) => getColumnCell(data?.objectId ?? "-"),
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "157px" },
      },
    },
    {
      key: LinkLogEntryOrderByFieldsEnum.ResourceChanges,
      header: t("link-changes-history.table.change-type"),
      renderCell: (data: LinkLogEntry) => getColumnCellList(data?.changeType),
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "180px" },
      },
    },
    {
      header: t("link-changes-history.table.action"),
      renderCell: (data: LinkLogEntry) => getColumnCellList(data?.change),
    },
    {
      key: LinkLogEntryOrderByFieldsEnum.UserId,
      header: t("link-changes-history.table.user"),
      renderCell: (data: LinkLogEntry) => getUserCell(data?.user),
      isSortable: true,
    },
  ];

  /**
   * Handles column header clicks for sorting
   * Updates global state with new sort parameters and triggers data refetch
   */
  const onHeaderClick = (column: TableSort) => {
    const newOrderBy = {
      direction: column.direction.toUpperCase() as OrderByDirection,
      field: column.column as any,
      header: "" as any,
    };

    dispatch({
      type: TableFiltersTypes.SetOrderBy,
      payload: newOrderBy,
    });
    onLoadLogEntries?.(1, newOrderBy, state?.tableFilters?.searchBy);
  };

  return (
    <div className={scss.linkHistoryTable}>
      <Table
        columns={columns as TableColumn[]}
        data={data}
        desktopVariant="regular"
        onSortChange={onHeaderClick}
        initialSort={
          {
            column: state?.tableFilters?.orderBy?.field,
            direction: state?.tableFilters?.orderBy?.direction?.toLocaleLowerCase() ?? "desc",
          } as TableSort
        }
      />

      {!!pagination?.total && (
        <Pager
          onChange={(page: number) => {
            onLoadLogEntries?.(page, state?.tableFilters?.orderBy, state?.tableFilters?.searchBy);
          }}
          showRowsPerPage={!!onChangeSizePerPage}
          pageSizeOptions={[10, 20]}
          onRowsPerPageChange={(size: { label: string; value: number }) =>
            handleChangeSizePerPage?.(size.value)
          }
          page={pagination?.page ?? 1}
          total={pagination?.total ?? 0}
          size={pagination?.size ?? 10}
          labelRowsPerPage={t("link-changes-history.pager.label-rows-per-page")}
          classname={{ global: scss.linkHistoryTable_pagerContainer }}
        />
      )}
    </div>
  );
};

export default LinkHistoryTable;
